import React, { FC } from 'react';
import { ErrorMessage, useField, useFormikContext } from 'formik';
import classNames from 'classnames';
import Dropdown from './Dropdown'; // Assuming Dropdown is in the same directory

interface TooltipIconData {
    mainIcon: string;
    closeIcon: string;
}

interface FormMultiSelectFieldProps {
    fieldName: string;
    label: string;
    options: { label: string; value: any }[];
    readOnly?: boolean;
    required?: boolean;
    dropdownProps?: Record<string, any>;
    tooltipIconData?: TooltipIconData;
    tooltip?: string;
    renderTooltipTextAsHtml?: boolean;
}

const FormMultiSelectField: React.FC<FormMultiSelectFieldProps> = ({
    fieldName,
    label,
    options,
    readOnly = false,
    required = false,
    dropdownProps = {},
    tooltipIconData,
    tooltip,
    renderTooltipTextAsHtml = false,
}) => {
    const { setFieldValue, values } = useFormikContext<any>();
    const [field, meta] = useField(fieldName);
    const hasError = !!(meta.touched && meta.error);

    const handleChange = (selectedOptions: any) => {
        const selectedValues = Array.isArray(selectedOptions)
            ? selectedOptions.map((option) => option.value)
            : [];
        setFieldValue(fieldName, selectedValues);
    };

    const selectedValues = Array.isArray(values[fieldName])
        ? values[fieldName].map((value: any) => options.find((option) => option.value === value))
        : [];

    return (
        <div
            className={classNames('FormBox FormSideBySide', {
                FormFieldRequired: required,
            })}
        >
            <div className="FormLabel">
                <label htmlFor={fieldName}>{label}</label>
                {tooltip && tooltipIconData && (
                    <div className="TooltipContainer">
                        <img
                            src={tooltipIconData.mainIcon}
                            alt="Tooltip Icon"
                            className="TooltipIcon"
                        />
                        {renderTooltipTextAsHtml ? (
                            <div
                                className="TooltipText"
                                dangerouslySetInnerHTML={{ __html: tooltip }}
                            />
                        ) : (
                            <div className="TooltipText">{tooltip}</div>
                        )}
                    </div>
                )}
            </div>
            <div className="FormField">
                <Dropdown
                    id={fieldName}
                    isMulti
                    options={options}
                    value={selectedValues}
                    onChange={handleChange}
                    readOnly={readOnly}
                    roundedRectangle
                    hasError={hasError}
                    {...dropdownProps}
                />
                <ErrorM name={fieldName} />
            </div>
        </div>
    );
};

export default FormMultiSelectField;

interface ErrorMProps {
    name: string;
}

export const ErrorM: FC<ErrorMProps> = ({ name }) => (
    <ErrorMessage name={name}>{(msg) => <div className="ErrorLabel">{msg}</div>}</ErrorMessage>
);
